import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleIdentityScript, resolveGoogleClientId } from '../../utils/googleGsi';

/**
 * Renders the official Google Sign-In button. Only one instance should be mounted per screen.
 */
const GoogleSignInButton = ({
  onCredential,
  text = 'signin_with',
  disabled = false,
  className = ''
}) => {
  const containerRef = useRef(null);
  const onCredentialRef = useRef(onCredential);
  const [status, setStatus] = useState('loading'); // loading | ready | unavailable
  const [message, setMessage] = useState('');

  onCredentialRef.current = onCredential;

  useEffect(() => {
    let cancelled = false;
    let mountNode = null;

    (async () => {
      const { clientId, enabled } = await resolveGoogleClientId();
      if (cancelled) return;
      if (!enabled || !clientId) {
        setStatus('unavailable');
        setMessage('');
        return;
      }
      try {
        await loadGoogleIdentityScript();
      } catch (e) {
        if (!cancelled) {
          setStatus('unavailable');
          setMessage('Could not load Google Sign-In');
        }
        return;
      }
      if (cancelled || !containerRef.current) return;
      mountNode = containerRef.current;

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          if (response?.credential) onCredentialRef.current(response.credential);
        },
        auto_select: false,
        cancel_on_tap_outside: true
      });

      window.google.accounts.id.renderButton(mountNode, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text,
        width: 320,
        locale: 'en'
      });
      setStatus('ready');
    })();

    return () => {
      cancelled = true;
      if (mountNode) {
        mountNode.innerHTML = '';
      }
    };
  }, [text]);

  if (status === 'unavailable' && !message) {
    return null;
  }

  return (
    <div className={`google-signin-wrap ${className}`.trim()}>
      {message ? <p className="error-message" style={{ marginBottom: '0.75rem' }}>{message}</p> : null}
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          justifyContent: 'center',
          minHeight: status === 'loading' ? 40 : undefined,
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto'
        }}
        aria-hidden={disabled}
      />
      {status === 'loading' ? (
        <p style={{ textAlign: 'center', color: '#666', fontSize: '0.875rem' }}>Loading Google Sign-In…</p>
      ) : null}
    </div>
  );
};

export default GoogleSignInButton;
